(() => {
  const offer = window.TFFCWelcome;
  if (!offer || offer.config.editor || !offer.config.allowed) return;
  const code = offer.config.code.toUpperCase();
  const track = (name, details) => {
    try {
      const destination = window.LittledataLayer?.googleAnalytics4?.measurementId;
      if (offer.config.mode !== 'campaigns' || !offer.active || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true || typeof window.gtag !== 'function' || !/^G-[A-Z0-9]+$/.test(destination || '')) return false;
      window.gtag('event', name, {
        send_to: destination, offer_code: code, discount_percentage: Number(offer.config.percentage),
        offer_state: offer.state, theme_id: String(offer.config.theme),
        page_type: window.themeVariables?.settings?.pageType || 'other', ...details
      });
      return true;
    } catch { return false; } // Analytics must never interrupt shopping.
  };
  // One display event per page; GA's Total users metric measures visitors across those displays.
  let trackingStarted = false;
  const startDisplayTracking = () => {
    if (trackingStarted || offer.config.mode !== 'campaigns' || !offer.active) return;
    trackingStarted = true;
    document.removeEventListener('welcome:change', startDisplayTracking);
    const visible = new Set();
    let sent = false, timer, attempts = 0;
    const check = () => {
      if (sent || !offer.active || document.visibilityState !== 'visible') return;
      const target = [...visible].find(element => element.isConnected && element.getClientRects().length &&
        (element.checkVisibility ? element.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true }) : getComputedStyle(element).visibility === 'visible'));
      if (!target) return;
      if (track('welcome_offer_view', { offer_surface: target.matches('[data-welcome-only]') ? 'message' : 'price' })) {
        sent = true;
        observer.disconnect(); mutations.disconnect(); clearTimeout(timer);
        for (const name of ['visitorConsentCollected', 'gtm_loaded', 'welcome:change', 'visibilitychange']) document.removeEventListener(name, check);
        window.removeEventListener('load', check);
      }
    };
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) entry.isIntersecting && entry.intersectionRatio > 0 ? visible.add(entry.target) : visible.delete(entry.target);
      check();
    });
    const selector = '.welcome-price__offer, [data-welcome-only]';
    const observe = root => {
      if (root.nodeType !== 1) return;
      if (root.matches(selector)) observer.observe(root);
      root.querySelectorAll(selector).forEach(element => observer.observe(element));
    };
    const mutations = new MutationObserver(records => {
      for (const record of records) record.addedNodes.forEach(observe);
    });
    observe(document.body);
    mutations.observe(document.body, { childList: true, subtree: true });
    for (const name of ['visitorConsentCollected', 'gtm_loaded', 'welcome:change', 'visibilitychange']) document.addEventListener(name, check);
    window.addEventListener('load', check);
    // Existing app loaders are asynchronous. Stop polling after ten seconds; consent/tag events still retry.
    const ready = () => { check(); if (!sent && ++attempts < 20) timer = setTimeout(ready, 500); };
    ready();
  };
  document.addEventListener('welcome:change', startDisplayTracking);
  startDisplayTracking();
  let leaving = false;
  window.addEventListener('pagehide', () => { leaving = true; });
  let pending, checked = '', attempted = false, confirmed = offer.wasApplied, revision = 0, checkoutRequested = false, refreshing = false;
  const normalise = value => String(value || '').trim().toUpperCase();
  const discounts = cart => [...(cart?.discount_codes || []).map(item => ({ code: normalise(item.code), applicable: item.applicable })),
    ...(cart?.cart_level_discount_applications || []).filter(item => item.type === 'discount_code').map(item => ({ code: normalise(item.title), applicable: item.total_allocated_amount > 0 })),
    ...(cart?.items || []).flatMap(item => (item.line_level_discount_allocations || []).filter(item => item.discount_application?.type === 'discount_code').map(item => ({ code: normalise(item.discount_application.title), applicable: item.amount > 0 })))];
  const prefix = normalise(offer.config.codePrefix);
  const compatible = value => value === code || Boolean(prefix && value.startsWith(prefix));
  const requested = new URLSearchParams(location.search).getAll('discount');
  const requestedCode = requested.length === 1 ? normalise(requested[0]) : '';
  if (requested.length && (!requestedCode || !compatible(requestedCode))) offer.setState('stopped');
  else if (requestedCode) offer.rememberCode(requestedCode);
  let requestedPending = Boolean(requestedCode && compatible(requestedCode));
  let selectedCode = compatible(normalise(offer.savedCode)) ? normalise(offer.savedCode) : code;
  const signature = cart => JSON.stringify((cart?.items || []).map(item => [item.key, item.quantity]));
  const accept = cart => {
    const codes = discounts(cart);
    if (offer.stopped) return true;
    if (codes.some(item => !compatible(item.code))) {
      offer.setState('stopped'); return true;
    }
    // An explicit email link takes precedence over an old default code in the basket.
    if (requestedPending && !codes.some(item => item.code === requestedCode)) return false;
    const match = codes.find(item => compatible(item.code) && item.applicable);
    if (match) {
      requestedPending = false;
      selectedCode = match.code; offer.rememberCode(selectedCode);
      confirmed = true; offer.setState('applied'); return true;
    }
    if (confirmed && cart.item_count > 0 && !codes.some(item => compatible(item.code))) {
      offer.rememberCode(''); offer.setState('stopped'); return true;
    }
    if (codes.length) {
      selectedCode = codes[0].code; offer.rememberCode(selectedCode);
      if (offer.state === 'applied') offer.setState('eligible');
    }
    return false;
  };
  const reconcile = async () => {
    const owner = window.TFFCCartState;
    if (!owner) return;
    const cart = await owner.read({ fresh: true });
    if (accept(cart) || !offer.active || offer.stopped || !cart.item_count || attempted) return;
    const current = signature(cart);
    if (current === checked) return;
    checked = current;
    const token = revision;
    const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
    url.searchParams.set('section_id', 'welcome-cart-state');
    const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(8000) });
    if (!response.ok) throw new Error('Cart eligibility unavailable');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const eligibility = JSON.parse(doc.querySelector('[data-welcome-cart]')?.textContent || '{}');
    if (revision !== token) { checked = ''; return; }
    if (!eligibility.items?.some(item => item.eligible)) return;
    if (JSON.stringify(eligibility.items.map(item => [item.key, item.quantity])) !== current) { checked = ''; return; }
    if (!offer.active || offer.stopped) return;
    if (discounts(cart).some(item => item.code === selectedCode && item.applicable === false)) {
      offer.setState('failed'); return;
    }
    attempted = true;
    offer.setState('applying');
    const updated = await owner.applyWelcomeDiscount(selectedCode);
    if (!accept(updated)) offer.setState('failed');
    else if (offer.state === 'applied' && window.themeVariables?.settings?.pageType === 'cart' && !checkoutRequested) {
      refreshing = true;
      location.reload();
    }
  };
  const sync = () => {
    if (pending) return pending;
    if (refreshing || !offer.active && !confirmed) return Promise.resolve();
    const start = revision;
    pending = reconcile().catch(() => { if (offer.active && !leaving) offer.setState('failed', false); }).finally(() => {
      pending = null;
      if (revision !== start && !attempted && offer.active) sync();
    });
    return pending;
  };
  document.addEventListener('welcome:change', () => { if (offer.active) sync(); });
  document.addEventListener('cart:updated', event => {
    revision++;
    const cart = event.detail?.cart;
    if (Array.isArray(cart?.items)) accept(cart);
    sync();
  });
  document.addEventListener('click', async event => {
    const save = event.target.closest?.('[data-welcome-save]');
    if (save && offer.config.form && typeof window._klOnsite?.push === 'function') {
      event.preventDefault(); window._klOnsite.push(['openForm', offer.config.form]);
      track('welcome_offer_save_click', { form_id: offer.config.form });
      return;
    }
    const checkout = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
    if (!checkout || !pending || !offer.active) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const nativeDrawer = checkout.closest('tffc-cart-drawer');
    checkoutRequested = true;
    await Promise.race([pending, new Promise(resolve => setTimeout(resolve, 8000))]);
    if (pending && offer.active) offer.setState('failed');
    const currentCheckout = checkout.isConnected ? checkout : nativeDrawer?.querySelector('button[name="checkout"]');
    currentCheckout?.click();
  }, true);
  window.addEventListener('pageshow', event => { leaving = false; if (event.persisted) sync(); });
  sync();
})();
